<template>
    <div id="editproducttype">
        <ui5-button
            id="cancelEditProducttype"
            class="top-button"
            @click="cancel()"
        >
            {{ trans('pages.edit_producttype.cancel') }}
        </ui5-button>
        <ui5-button
            :disabled="loading"
            id="saveEditProducttype"
            class="top-button"
            @click="save()"
        >
            {{ trans('pages.edit_producttype.save') }}
        </ui5-button>
        <div style="clear: both" />
        <loader
            v-if="loading"
            :message="trans('pages.edit_producttype.loading')"
        />
        <loader
            v-else-if="saving"
            :message="trans('pages.edit_producttype.saving')"
        />
        <template v-else>
            <pre
                v-if="isDebug"
                style="float: right; white-space: pre-wrap"
                >{{ productType }} {{ savePayload }}</pre
            >
            <div
                id="editProducttypeForm"
                v-if="productType"
            >
                <h2
                    id="producttypeSubtitle"
                    v-if="isNew"
                >
                    {{ trans('pages.edit_producttype.add_title') }}
                </h2>
                <h2
                    id="producttypeSubtitle"
                    v-else
                >
                    {{ trans('pages.edit_producttype.update_title') }}
                </h2>

                <div
                    id="l_label"
                    class="product-type-field label"
                >
                    <ui5-label>
                        {{ trans('pages.edit_producttype.external_name') }}
                    </ui5-label>
                    <translations-edit
                        ref="translations-label"
                        :translation-data="productType.translations.label"
                    ></translations-edit>
                </div>

                <div
                    id="f_name"
                    class="product-type-field"
                >
                    <ui5-label for="name">
                        {{ trans('pages.edit_producttype.key') }}
                    </ui5-label>
                    <ui5-input
                        type="Text"
                        id="name"
                        :value="productType.name"
                        @input="productType.name = $event.target.value"
                        :readonly="!isNew"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.name)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.name) }}
                    </div>
                </div>

                <div
                    id="f_code"
                    class="product-type-field"
                >
                    <ui5-label for="code">
                        {{ trans('pages.edit_producttype.code') }}
                    </ui5-label>
                    <ui5-input
                        type="Text"
                        id="code"
                        :value="productType.code"
                        @input="productType.code = $event.target.value"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.code)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.code) }}
                    </div>
                </div>

                <div class="product-type-field">
                    <ui5-label for="customImage">
                        {{ trans('pages.edit_producttype.custom_image') }}
                    </ui5-label>
                    <ui5-select
                        id="customImage"
                        @change="
                            productType.customImage =
                                $event.detail.selectedOption.value || null
                        "
                    >
                        <ui5-option
                            :value="null"
                            :selected="productType.customImage == null"
                        >
                            ---
                        </ui5-option>
                        <ui5-option
                            v-for="option in customImageOptions"
                            :key="option.key"
                            :selected="productType.customImage == option.key"
                            :value="option.key"
                        >
                            {{ option.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="getErrorFromValidator($v.productType.customImage)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.customImage) }}
                    </div>
                </div>

                <div class="product-type-field image-field">
                    <ui5-label for="image">
                        {{ trans('pages.edit_producttype.image') }}
                    </ui5-label>
                    <div>
                        <img
                            v-if="productType"
                            :src="getImageSrc()"
                            @error="
                                $event.target.src = 'images/placeholder.png'
                            "
                        />
                        <ui5-file-uploader
                            id="image"
                            @change="imageChange"
                            v-if="!removeImage"
                        >
                            <ui5-button>
                                {{
                                    trans(
                                        'pages.edit_producttype.upload_new_image'
                                    )
                                }}
                            </ui5-button>
                        </ui5-file-uploader>
                        <div
                            v-if="getErrorFromValidator($v.imageFile)"
                            class="error"
                        >
                            {{ getErrorFromValidator($v.imageFile) }}
                        </div>
                        <ui5-checkbox
                            :text="trans('pages.edit_producttype.remove_image')"
                            :checked="removeImage"
                            @change="removeImage = $event.target.checked"
                        />
                    </div>
                </div>

                <div
                    id="f_sortBy"
                    class="product-type-field"
                >
                    <ui5-label for="sortBy">
                        {{ trans('pages.edit_producttype.order') }}
                    </ui5-label>
                    <text-input
                        type="number"
                        id="sortBy"
                        min="0"
                        max="999"
                        v-model="productType.sortBy"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.sortBy)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.sortBy) }}
                    </div>
                </div>

                <div
                    id="f_cncDirectory"
                    class="product-type-field"
                >
                    <ui5-label for="cncDirectory">
                        {{ trans('pages.edit_producttype.cnc_directory') }}
                    </ui5-label>
                    <ui5-input
                        type="Text"
                        id="cncDirectory"
                        :value="productType.cncDirectory"
                        @input="productType.cncDirectory = $event.target.value"
                    />
                    <div
                        v-if="
                            getErrorFromValidator($v.productType.cncDirectory)
                        "
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.cncDirectory) }}
                    </div>
                </div>

                <div
                    id="x_initialState"
                    class="product-type-field"
                >
                    <ui5-label for="initialState">
                        {{ trans('pages.edit_producttype.initial_status') }}
                    </ui5-label>
                    <ui5-select
                        id="initialState"
                        @change="
                            productType.initialState =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in stateOptions"
                            :key="item.value"
                            :selected="item.value == productType.initialState"
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="
                            getErrorFromValidator($v.productType.initialState)
                        "
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.initialState) }}
                    </div>
                </div>

                <div
                    id="x_supplierId"
                    class="product-type-field"
                >
                    <ui5-label for="supplierId">
                        {{ trans('pages.edit_producttype.supplier') }}
                    </ui5-label>
                    <ui5-select
                        id="supplierId"
                        @change="
                            productType.supplierId =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in supplierOptions"
                            :key="item.value"
                            :selected="item.value == productType.supplierId"
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="getErrorFromValidator($v.productType.supplierId)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.supplierId) }}
                    </div>
                </div>

                <div
                    id="x_producttypegroup"
                    class="product-type-field"
                    v-if="isDebug"
                >
                    <ui5-label for="producttypegroup">
                        {{ trans('pages.edit_producttype.group') }}
                    </ui5-label>
                    <ui5-select
                        id="producttypegroup"
                        @change="
                            productType.profilegroup =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in groupOptions"
                            :key="item.value"
                            :selected="item.value == productType.profilegroup"
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="
                            getErrorFromValidator($v.productType.profilegroup)
                        "
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.profilegroup) }}
                    </div>
                </div>

                <div
                    id="x_producttype_group_id"
                    class="product-type-field"
                >
                    <ui5-label for="producttype_group_id">
                        {{ trans('pages.edit_producttype.product_type_group') }}
                    </ui5-label>
                    <auto-complete-input
                        v-if="productTypeGroupOptions"
                        id="producttypeGroupInput"
                        name="product-type-group-id"
                        :value="productType.productTypeGroupId"
                        :options="productTypeGroupOptions"
                        :initial-search="
                            getProductTypeGroupName(
                                productType.productTypeGroupId
                            )
                        "
                        :placeholder="
                            trans('pages.edit_producttype.select_group')
                        "
                        :lbl-no-options="
                            trans('pages.edit_producttype.no_groups_found')
                        "
                        @input="productType.productTypeGroupId = $event"
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.producttype_group_id
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.producttype_group_id
                            )
                        }}
                    </div>
                </div>

                <div
                    v-if="isDebug"
                    id="x_producttypesubgroup"
                    class="product-type-field"
                >
                    <ui5-label for="producttypesubgroup">
                        {{ trans('pages.edit_producttype.subgroup') }}
                    </ui5-label>
                    <ui5-select
                        id="producttypesubgroup"
                        @change="
                            productType.profilesubgroup =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in subgroupOptions"
                            :key="item.value"
                            :selected="
                                item.value == productType.profilesubgroup
                            "
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.profilesubgroup
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.profilesubgroup
                            )
                        }}
                    </div>
                </div>

                <div
                    id="x_defaultFlow"
                    class="product-type-field"
                >
                    <ui5-label for="defaultFlow">
                        {{ trans('pages.edit_producttype.default_flow') }}
                    </ui5-label>
                    <ui5-select
                        id="defaultFlow"
                        @change="
                            productType.defaultFlow =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in flowOptions"
                            :key="item.value"
                            :selected="item.value == productType.defaultFlow"
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="getErrorFromValidator($v.productType.defaultFlow)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.defaultFlow) }}
                    </div>
                </div>

                <div
                    id="x_orderType"
                    class="product-type-field"
                >
                    <ui5-label for="orderType">
                        {{ trans('pages.edit_producttype.order_type') }}
                    </ui5-label>
                    <ui5-select
                        id="orderType"
                        @change="
                            productType.orderType =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in orderTypeOptions"
                            :key="item.value"
                            :selected="item.value == productType.orderType"
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="getErrorFromValidator($v.productType.orderType)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.orderType) }}
                    </div>
                </div>

                <div
                    id="x_workstationType"
                    class="product-type-field"
                >
                    <ui5-label for="workstationType">
                        {{ trans('pages.edit_producttype.workstation_type') }}
                    </ui5-label>
                    <ui5-select
                        id="workstationType"
                        @change="
                            productType.workstationTypeId =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in workstationTypeOptions"
                            :key="item.value"
                            :selected="
                                item.value == productType.workstationTypeId
                            "
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.workstationTypeId
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.workstationTypeId
                            )
                        }}
                    </div>
                </div>

                <div
                    id="c_active"
                    class="product-type-field"
                >
                    <ui5-label for="active">
                        {{ trans('pages.edit_producttype.active') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="active"
                        :checked="productType.active"
                        @change="productType.active = $event.target.checked"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.active)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.active) }}
                    </div>
                </div>

                <div
                    id="c_autoProcess"
                    class="product-type-field"
                >
                    <ui5-label for="autoProcess">
                        {{ trans('pages.edit_producttype.auto_process') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="autoProcess"
                        :checked="productType.autoProcess"
                        @change="
                            productType.autoProcess = $event.target.checked
                        "
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.autoProcess)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.autoProcess) }}
                    </div>
                </div>

                <div
                    id="c_showOnProsite"
                    class="product-type-field"
                >
                    <ui5-label for="showOnProsite">
                        {{ trans('pages.edit_producttype.show_on_prosite') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="showOnProsite"
                        :checked="productType.showOnProsite"
                        @change="
                            productType.showOnProsite = $event.target.checked
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator($v.productType.showOnProsite)
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator($v.productType.showOnProsite)
                        }}
                    </div>
                </div>

                <div
                    id="c_allowTemplates"
                    class="product-type-field"
                >
                    <ui5-label for="allowTemplates">
                        {{ trans('pages.edit_producttype.allow_templates') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="allowTemplates"
                        :checked="productType.allowTemplates"
                        @change="
                            productType.allowTemplates = $event.target.checked
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator($v.productType.allowTemplates)
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator($v.productType.allowTemplates)
                        }}
                    </div>
                </div>

                <div
                    id="c_paintlistAsWindow"
                    class="product-type-field"
                >
                    <ui5-label for="paintlistAsWindow">
                        {{
                            trans('pages.edit_producttype.paintlist_as_window')
                        }}
                    </ui5-label>
                    <ui5-checkbox
                        id="paintlistAsWindow"
                        :checked="productType.paintlistAsWindow"
                        @change="
                            productType.paintlistAsWindow =
                                $event.target.checked
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.paintlistAsWindow
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.paintlistAsWindow
                            )
                        }}
                    </div>
                </div>

                <div
                    id="c_separatePaintlist"
                    class="product-type-field"
                >
                    <ui5-label for="separatePaintlist">
                        {{ trans('pages.edit_producttype.separate_paintlist') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="separatePaintlist"
                        :checked="productType.separatePaintlist"
                        @change="
                            productType.separatePaintlist =
                                $event.target.checked
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.separatePaintlist
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.separatePaintlist
                            )
                        }}
                    </div>
                </div>

                <div
                    id="c_allowBulkCopy"
                    class="product-type-field"
                >
                    <ui5-label
                        for="allowBulkCopy"
                        ref="allowBulkCopyLabel"
                    >
                        {{ trans('pages.edit_producttype.allow_bulk_copy') }}
                        <i
                            v-if="!hasRequiredBulkCopyDatafields"
                            class="fa fa-question-circle"
                            @mouseout="$refs.tooltipAllowBulkCopy.close()"
                            @mouseover="
                                $refs.tooltipAllowBulkCopy.showAt(
                                    $refs.allowBulkCopyLabel
                                )
                            "
                        />
                    </ui5-label>
                    <ui5-popover ref="tooltipAllowBulkCopy">
                        {{
                            trans(
                                'pages.edit_producttype.allow_bulk_copy_tooltip'
                            )
                        }}
                    </ui5-popover>
                    <ui5-checkbox
                        id="allowBulkCopy"
                        :checked="productType.allowBulkCopy"
                        :readonly="!hasRequiredBulkCopyDatafields"
                        @change="
                            productType.allowBulkCopy = $event.target.checked
                        "
                    />
                </div>

                <div
                    id="l_productDescription"
                    class="product-type-field productDescription"
                    v-if="!isNew"
                >
                    <ui5-label>
                        {{
                            trans('pages.edit_producttype.product_description')
                        }}
                    </ui5-label>
                    <translations-edit-datafield-autocomplete
                        ref="productDescriptionEditor"
                        :validate-html="true"
                        :datafields="datafields"
                        :translation-data="
                            productType.translations.productDescription
                        "
                    ></translations-edit-datafield-autocomplete>
                </div>

                <div
                    id="l_prositeDescription"
                    class="product-type-field productDescription"
                    v-if="!isNew"
                >
                    <ui5-label>
                        {{
                            trans('pages.edit_producttype.prosite_description')
                        }}
                    </ui5-label>
                    <translations-edit-datafield-autocomplete
                        ref="prositeDescriptionEditor"
                        :validate-html="true"
                        :datafields="datafields"
                        :translation-data="
                            productType.translations.prositeDescription
                        "
                    ></translations-edit-datafield-autocomplete>
                </div>

                <div
                    id="l_compactDescription"
                    class="product-type-field productDescription"
                    v-if="!isNew"
                >
                    <ui5-label>
                        {{
                            trans(
                                'pages.edit_producttype.prosite_description_compact'
                            )
                        }}
                    </ui5-label>
                    <translations-edit-datafield-autocomplete
                        ref="compactDescriptionEditor"
                        :validate-html="true"
                        :datafields="datafields"
                        :translation-data="
                            productType.translations.compactDescription
                        "
                    ></translations-edit-datafield-autocomplete>
                </div>

                <div
                    id="l_quoteDescription"
                    class="product-type-field productDescription"
                    v-if="!isNew"
                >
                    <ui5-label>
                        {{ trans('pages.edit_producttype.quote_description') }}
                    </ui5-label>
                    <translations-edit-datafield-autocomplete
                        ref="quoteDescriptionEditor"
                        :validate-html="true"
                        :datafields="datafields"
                        :translation-data="
                            productType.translations.quoteDescription
                        "
                    ></translations-edit-datafield-autocomplete>
                </div>

                <div
                    id="x_paintlistDescription"
                    class="product-type-field simple-textarea"
                    v-if="!isNew"
                >
                    <ui5-label for="paintlistDescription">
                        {{
                            trans(
                                'pages.edit_producttype.paintlist_description'
                            )
                        }}
                    </ui5-label>
                    <description-editor
                        id="paintlistDescription"
                        v-model="productType.paintlistDescription"
                        :datafields="datafields"
                    ></description-editor>
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.paintlistDescription
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.paintlistDescription
                            )
                        }}
                    </div>
                </div>

                <div
                    class="product-type-field simple-textarea"
                    v-if="!isNew"
                >
                    <ui5-label for="area-formula">
                        {{
                            trans(
                                'product_types.area_calculation_formula.label'
                            )
                        }}
                    </ui5-label>
                    <description-editor
                        id="area-formula"
                        v-model="productType.areaCalculationFormula"
                        :datafields="datafields"
                        :placeholder="
                            trans(
                                'product_types.area_calculation_formula.placeholder'
                            )
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.areaCalculationFormula
                            )
                        "
                        class="error"
                    >
                        {{
                            trans(
                                getErrorFromValidator(
                                    $v.productType.areaCalculationFormula
                                )
                            )
                        }}
                    </div>
                </div>

                <div
                    class="product-type-field simple-textarea"
                    v-if="!isNew"
                >
                    <ui5-label
                        for="area-formula"
                        ref="areaFormula"
                    >
                        {{
                            trans(
                                'product_types.paint_area_calculation_formula.label'
                            )
                        }}

                        <i
                            class="fa fa-question-circle"
                            @mouseout="
                                $refs.toolTipPaintAreaCalculationFormula.close()
                            "
                            @mouseover="
                                $refs.toolTipPaintAreaCalculationFormula.showAt(
                                    $refs.areaFormula
                                )
                            "
                        />
                    </ui5-label>

                    <ui5-popover
                        ref="toolTipPaintAreaCalculationFormula"
                        v-html="
                            trans(
                                'pages.edit_producttype.paint_area_calculation_formula_tooltip'
                            )
                        "
                    ></ui5-popover>

                    <description-editor
                        id="area-formula"
                        v-model="productType.paintAreaCalculationFormula"
                        :datafields="datafields"
                        :placeholder="
                            trans(
                                'product_types.paint_area_calculation_formula.placeholder'
                            )
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.paintAreaCalculationFormula
                            )
                        "
                        class="error"
                    >
                        {{
                            trans(
                                getErrorFromValidator(
                                    $v.productType.paintAreaCalculationFormula
                                )
                            )
                        }}
                    </div>
                </div>

                <div class="product-type-field">
                    <ui5-label for="dimensions">
                        {{ trans('pages.edit_producttype.dimensions') }}
                    </ui5-label>
                    <description-editor
                        id="dimensions"
                        v-model="productType.dimensions"
                        :datafields="datafields"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.dimensions)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.dimensions) }}
                    </div>
                </div>

                <div class="product-type-field">
                    <ui5-label
                        for="number-of-product-labels"
                        required
                    >
                        {{
                            trans(
                                'pages.edit_producttype.number_of_product_labels'
                            )
                        }}
                    </ui5-label>
                    <text-input
                        id="number-of-product-labels"
                        type="number"
                        min="1"
                        step="1"
                        @input="
                            productType.numberOfProductLabels = $event
                                ? parseInt($event)
                                : ''
                        "
                        :value="productType.numberOfProductLabels"
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.numberOfProductLabels
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.numberOfProductLabels
                            )
                        }}
                    </div>
                </div>

                <div class="product-type-field">
                    <ui5-label
                        for="colli"
                        required
                    >
                        {{ trans('pages.edit_producttype.colli') }}
                    </ui5-label>
                    <text-input
                        id="colli"
                        type="number"
                        min="1"
                        step="1"
                        @input="
                            productType.colli = $event ? parseInt($event) : ''
                        "
                        :value="productType.colli"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.colli)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.colli) }}
                    </div>
                </div>

                <div
                    id="c_isPleating"
                    class="product-type-field"
                >
                    <ui5-label for="isPleating">
                        {{ trans('pages.edit_producttype.is_pleating') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="isPleating"
                        :checked="productType.isPleating"
                        @change="productType.isPleating = $event.target.checked"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.isPleating)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.isPleating) }}
                    </div>
                </div>

                <div
                    id="x_pleatingOverviewInfo"
                    class="pleatingField product-type-field"
                    v-if="!isNew && productType.isPleating"
                >
                    <ui5-label for="pleatingOverviewInfo">
                        {{
                            trans(
                                'pages.edit_producttype.pleating_overview_info'
                            )
                        }}
                    </ui5-label>
                    <ui5-select
                        id="pleatingOverviewInfo"
                        @change="
                            productType.pleatingOverviewInfo =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in datafieldOptions"
                            :key="item.value"
                            :selected="
                                item.value == productType.pleatingOverviewInfo
                            "
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.pleatingOverviewInfo
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.pleatingOverviewInfo
                            )
                        }}
                    </div>
                </div>

                <div
                    id="x_pleatingCreationFilter"
                    class="product-type-field pleatingField"
                    v-if="!isNew && productType.isPleating"
                >
                    <ui5-label
                        for="pleatingCreationFilter"
                        ref="pleatingCreationFilterLabel"
                    >
                        {{
                            trans(
                                'pages.edit_producttype.pleating_creation_filter'
                            )
                        }}
                        <i
                            class="fa fa-question-circle"
                            @mouseout="
                                $refs.tooltipPleatingCreationFilter.close()
                            "
                            @mouseover="
                                $refs.tooltipPleatingCreationFilter.showAt(
                                    $refs.pleatingCreationFilterLabel
                                )
                            "
                        />
                    </ui5-label>
                    <ui5-popover
                        ref="tooltipPleatingCreationFilter"
                        v-html="
                            trans(
                                'pages.edit_producttype.pleating_creation_filter_tooltip'
                            )
                        "
                    ></ui5-popover>
                    <ui5-select
                        id="pleatingCreationFilter"
                        @change="
                            productType.pleatingCreationFilter =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-for="item in datafieldOptions"
                            :key="item.value"
                            :selected="
                                item.value == productType.pleatingCreationFilter
                            "
                            :value="item.value"
                        >
                            {{ item.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.pleatingCreationFilter
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.pleatingCreationFilter
                            )
                        }}
                    </div>
                </div>

                <div
                    id="c_optimizePaintlists"
                    class="product-type-field"
                >
                    <ui5-label for="optimizePaintlists">
                        {{
                            trans('pages.edit_producttype.optimize_paintlists')
                        }}
                    </ui5-label>
                    <ui5-checkbox
                        id="optimizePaintlists"
                        :checked="productType.optimizePaintlists"
                        @change="
                            productType.optimizePaintlists =
                                $event.target.checked
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.optimizePaintlists
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.optimizePaintlists
                            )
                        }}
                    </div>
                </div>

                <div
                    id="c_colorRequired"
                    class="product-type-field"
                >
                    <ui5-label for="colorRequired">
                        {{ trans('pages.edit_producttype.color_required') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="colorRequired"
                        :checked="productType.colorRequired"
                        @change="
                            productType.colorRequired = $event.target.checked
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator($v.productType.colorRequired)
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator($v.productType.colorRequired)
                        }}
                    </div>
                </div>

                <div
                    id="c_immediatelyFinish"
                    class="product-type-field"
                >
                    <ui5-label for="immediatelyFinish">
                        {{ trans('pages.edit_producttype.immediately_finish') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="immediatelyFinish"
                        :checked="productType.immediatelyFinish"
                        @change="
                            productType.immediatelyFinish =
                                $event.target.checked
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.immediatelyFinish
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.immediatelyFinish
                            )
                        }}
                    </div>
                </div>

                <div
                    id="c_allowColor"
                    class="product-type-field"
                >
                    <ui5-label for="allowColor">
                        {{ trans('pages.edit_producttype.allow_color') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="allowColor"
                        :checked="productType.allowColor"
                        @change="productType.allowColor = $event.target.checked"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.allowColor)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.allowColor) }}
                    </div>
                </div>

                <div
                    id="c_allowCanvas"
                    class="product-type-field"
                >
                    <ui5-label for="allowCanvas">
                        {{ trans('pages.edit_producttype.allow_canvas') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="allowCanvas"
                        :checked="productType.allowCanvas"
                        @change="
                            productType.allowCanvas = $event.target.checked
                        "
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.allowCanvas)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.allowCanvas) }}
                    </div>
                </div>

                <div
                    id="c_allowCount"
                    class="product-type-field"
                >
                    <ui5-label for="allowCount">
                        {{ trans('pages.edit_producttype.allow_count') }}
                    </ui5-label>
                    <ui5-checkbox
                        id="allowCount"
                        :checked="productType.allowCount"
                        @change="productType.allowCount = $event.target.checked"
                    />
                    <div
                        v-if="getErrorFromValidator($v.productType.allowCount)"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.productType.allowCount) }}
                    </div>
                </div>

                <div
                    id="c_allowTechnicalFiles"
                    class="product-type-field"
                >
                    <ui5-label for="allowTechnicalFiles">
                        {{
                            trans(
                                'pages.edit_producttype.allow_technical_files'
                            )
                        }}
                    </ui5-label>
                    <ui5-checkbox
                        id="allowTechnicalFiles"
                        :checked="productType.allowTechnicalFiles"
                        @change="
                            productType.allowTechnicalFiles =
                                $event.target.checked
                        "
                    />
                    <div
                        v-if="
                            getErrorFromValidator(
                                $v.productType.allowTechnicalFiles
                            )
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator(
                                $v.productType.allowTechnicalFiles
                            )
                        }}
                    </div>
                </div>

                <div
                    id="c_attachmentRule"
                    class="product-type-field"
                >
                    <ui5-label for="attachmentRule">
                        {{ trans('pages.edit_producttype.attachments') }}
                    </ui5-label>
                    <ui5-select
                        id="attachmentRule"
                        @change="
                            productType.attachmentRule =
                                $event.detail.selectedOption.value || null
                        "
                    >
                        <ui5-option
                            :value="null"
                            :selected="productType.attachmentRule == null"
                        >
                            ---
                        </ui5-option>
                        <ui5-option
                            v-for="option in productAttachmentRules.getOptions()"
                            :key="option.value"
                            :selected="
                                productType.attachmentRule == option.value
                            "
                            :value="option.value"
                        >
                            {{ option.label }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        v-if="
                            getErrorFromValidator($v.productType.attachmentRule)
                        "
                        class="error"
                    >
                        {{
                            getErrorFromValidator($v.productType.attachmentRule)
                        }}
                    </div>
                </div>

                <h3 class="rules-title">
                    {{ trans('pages.edit_producttype.rules') }}
                    <i
                        class="fa fa-question-circle"
                        @mouseover="
                            $refs.tooltipValidationRulesInfo.showAt(
                                $event.target
                            )
                        "
                        @mouseout="$refs.tooltipValidationRulesInfo.close()"
                    />
                    <ui5-popover
                        ref="tooltipValidationRulesInfo"
                        placement-type="Bottom"
                        :allow-target-overlap="false"
                    >
                        <p>{{ trans('pages.edit_producttype.info') }}</p>
                    </ui5-popover>
                    <i
                        @click="addRule()"
                        class="fa fa-plus"
                    ></i>
                </h3>

                <draggable
                    :list="datafieldValidationRules"
                    handle=".handle"
                >
                    <datafield-validation-rule
                        @onMoveUp="onOrderMoveUp"
                        @onMoveDown="onOrderMoveDown"
                        v-for="(rule, index) of datafieldValidationRules"
                        :key="index"
                        :index="index"
                        :last-index="datafieldValidationRules.length - 1"
                        :rule="rule"
                        :datafields="datafields"
                        :validation-errors="
                            $v.datafieldValidationRules.$each[index]
                        "
                        @delete="deleteRule(index)"
                    />
                </draggable>
            </div>
        </template>
    </div>
</template>
<script>
import ErrorHelper from '../helpers/ErrorHelper';
import {
    maxLength,
    minLength,
    required,
    requiredIf,
    minValue,
} from 'vuelidate/lib/validators';
import { validAreaCalculationFormula } from '../validators/validAreaCalculationFormula';
import { validPaintAreaCalculationFormula } from '../validators/validPaintAreaCalculationFormula';
import TranslationsEdit from './../components/TranslationsEdit';
import TranslationsEditDatafieldAutocomplete from '../components/TranslationsEditDatafieldAutocomplete';
import DescriptionEditor from '../components/productTypes/DescriptionEditor';
import productTypeApi from '../api/productType';
import languageApi from '../api/language';
import statusApi from '../api/status';
import orderApi from '../api/order';
import productTypeGroupApi from '../api/productTypeGroup';
import workstationApi from '../api/workstation';
import supplierApi from '../api/supplier';
import flowApi from '../api/flow';
import SpinnerText from '../components/loader/SpinnerText';
import IsDebugMixin from '../mixins/is-debug';
import IdParamMixin from '../mixins/id-param';
import Loader from '../components/loader/Loader';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import DatafieldValidationRule from '../components/datafieldValidation/DatafieldValidationRule.vue';
import draggable from 'vuedraggable';
import productAttachmentRules from '../shared/productAttachmentRules';
import localization from '../mixins/localization';
import TextInput from '../components/TextInput';
import AutoCompleteInput from '../components/AutoCompleteInput';
import { getItemPath } from '../shared/nestedStructure';
import { getLabel, byLabelSorter } from '../shared/collections';
import { BULK_COPY_REQUIRED_DATAFIELD_KEYS } from '../shared/datafield';

export default {
    components: {
        DatafieldValidationRule,
        Loader,
        SpinnerText,
        TranslationsEditDatafieldAutocomplete,
        TranslationsEdit,
        DescriptionEditor,
        draggable,
        TextInput,
        AutoCompleteInput,
    },
    data() {
        return {
            loading: false,
            saving: false,
            productTypeId: null,
            productType: null,
            datafieldValidationRules: [],
            stateOptions: null,
            orderTypeOptions: null,
            workstationTypeOptions: null,
            flowOptions: null,
            supplierOptions: null,
            groupOptions: null,
            subgroupOptions: null,
            productTypeGroups: null,
            datafieldOptions: null,
            datafields: null,
            removeImage: false,
            imageFile: null,
            imageFilePreview: null,
            customImageOptions: null,
        };
    },
    validations() {
        const statementValidations = {
            datafieldId: { required },
            cmpOperator: { required },
            // If condition OR action === 'SET' required
            // One comparator must be filled
            cmpValue: {
                required: requiredIf(
                    (model) =>
                        model.cmpDatafieldId !== null &&
                        model.cmpDatafieldOptionId !== null &&
                        (model.action === 'SET' || model.action === undefined)
                ),
            },
            cmpDatafieldId: {
                required: requiredIf(
                    (model) =>
                        model.cmpValue !== null &&
                        model.cmpDatafieldOptionId !== null &&
                        (model.action === 'SET' || model.action === undefined)
                ),
            },
            cmpDatafieldOptionId: {
                required: requiredIf(
                    (model) =>
                        model.cmpValue !== null &&
                        model.cmpDatafieldId !== null &&
                        (model.action === 'SET' || model.action === undefined)
                ),
            },
            // If action operator is filled
            // One action must be filled
            actionOperator: {
                required: requiredIf(
                    (model) =>
                        model.actionValue !== null ||
                        model.actionDatafieldId !== null ||
                        model.actionDatafieldOptionId !== null
                ),
            },
            actionValue: {
                required: requiredIf(
                    (model) =>
                        model.actionOperator !== null &&
                        model.actionDatafieldId === null &&
                        model.actionDatafieldOptionId === null
                ),
            },
            actionDatafieldId: {
                required: requiredIf(
                    (model) =>
                        model.actionOperator !== null &&
                        model.actionValue === null &&
                        model.actionDatafieldOptionId === null
                ),
            },
            actionDatafieldOptionId: {
                required: requiredIf(
                    (model) =>
                        model.actionOperator !== null &&
                        model.actionValue === null &&
                        model.actionDatafieldId === null
                ),
            },
        };

        return {
            productType: {
                name: {
                    required,
                    maxLength: maxLength(50),
                    minLength: minLength(1),
                },
                numberOfProductLabels: {
                    required,
                    minValue: minValue(1),
                },
                colli: {
                    required,
                    minValue: minValue(1),
                },
                customImage: {
                    //required,
                },
                areaCalculationFormula: {
                    validAreaCalculationFormula,
                },
                paintAreaCalculationFormula: {
                    validPaintAreaCalculationFormula,
                },
                producttype_group_id: {
                    // Optional, no validation required
                },
            },
            imageFile: {
                required: requiredIf(
                    () => !this.productType.imagePathCloudFront
                ),
            },
            datafieldValidationRules: {
                $each: {
                    datafieldValidationConditions: {
                        $each: {
                            ...statementValidations,
                        },
                    },
                    datafieldValidationActions: {
                        $each: {
                            action: { required },
                            ...statementValidations,
                        },
                    },
                },
            },
        };
    },
    methods: {
        getErrorFromValidator: ErrorHelper.getErrorFromValidator,
        getProductTypeGroupName(id) {
            return getLabel(id, this.productTypeGroupOptions);
        },
        getImageSrc() {
            const placeholder = 'images/placeholder.png';
            if (this.removeImage) {
                return placeholder;
            }
            if (this.imageFilePreview) {
                return this.imageFilePreview;
            }
            return this.productType.imagePathCloudFront
                ? this.productType.imagePathCloudFront
                : placeholder;
        },
        imageChange($event) {
            if ($event.detail && $event.detail.files) {
                const file = $event.detail.files[0];
                this.imageFile = file;
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    this.imageFilePreview = reader.result;
                };
                reader.onerror = (error) => {
                    showErrorMessage(
                        this.trans(
                            'pages.edit_producttype.error_generate_preview'
                        )
                    );
                    this.imageFile = null;
                };
            }

            function getBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = (error) => reject(error);
                });
            }
        },
        isValid() {
            let valid = true;
            [this, ...Object.values(this.$refs).flat()].forEach((x) => {
                if (x.$v && x.$v.$invalid) {
                    valid = false;
                }
            });
            return valid;
        },
        newType() {
            const emptyTranslations = () => {
                const translations = {};
                this.languages.forEach((x) => {
                    translations[x.code] = '';
                });
                return translations;
            };
            return {
                label: '',
                name: '',
                code: '',
                sortBy: '',
                customImage: '',
                cncDirectory: '',
                initialState: this.stateOptions[0].value,
                supplierId: this.supplierOptions[0].value,
                profilegroup: this.groupOptions[0].value,
                profilesubgroup: this.subgroupOptions[0].value,
                defaultFlow: this.flowOptions[0].value,
                orderType: this.orderTypeOptions[0].value,
                workstationTypeId: this.workstationTypeOptions[0].value,
                productTypeGroupId: null,
                active: false,
                autoProcess: false,
                showOnProsite: false,
                allowTemplates: false,
                translations: {
                    label: emptyTranslations(),
                    productDescription: emptyTranslations(),
                    prositeDescription: emptyTranslations(),
                    quoteDescription: emptyTranslations(),
                    compactDescription: emptyTranslations(),
                },
                paintlistDescription: '',
                isPleating: false,
                pleatingOverviewInfo: this.datafieldOptions
                    ? this.datafieldOptions[0].value
                    : null,
                pleatingCreationFilter: this.datafieldOptions
                    ? this.datafieldOptions[0].value
                    : null,
                optimizePaintlists: true,
                colorRequired: true,
                immediatelyFinish: false,
                areaCalculationFormula: '',
                paintAreaCalculationFormula: '',
                dimensions: '',
                numberOfProductLabels: 1,
                colli: 1,
                paintlistAsWindow: false,
                separatePaintlist: false,
                allowBulkCopy: false,
            };
        },
        cancel() {
            this.$router.go(-1);
        },
        save() {
            [this, ...Object.values(this.$refs).flat()].forEach((x) => {
                if (x.$v) {
                    x.$v.$touch();
                }
            });

            if (!this.isValid()) {
                return showErrorMessage(
                    this.trans('pages.edit_producttype.error_fix_input_errors')
                );
            }

            const payload = this.savePayload;
            const isNew = this.isNew;

            const imagePromises = [];

            if (
                this.productType.imagePathS3 &&
                (this.removeImage || this.imageFile)
            ) {
                imagePromises.push(
                    productTypeApi
                        .deleteProductTypeImage(this.productType.imagePathS3)
                        .then(() => {
                            payload.imagePathS3 = null;
                        })
                );
            }

            if (this.imageFile) {
                imagePromises.push(
                    productTypeApi
                        .uploadProductTypeImage(this.imageFile)
                        .then((response) => {
                            payload.imagePathS3 = response.data.fileName;
                        })
                );
            }

            this.saving = true;

            Promise.all(imagePromises)
                .then(() => {
                    return isNew
                        ? productTypeApi.createType(payload)
                        : productTypeApi.updateType(payload);
                })
                .then((response) => {
                    const id = isNew
                        ? response.data.data.newId
                        : this.productType.producttypeId;

                    return productTypeApi
                        .updateDatafieldValidationRules(
                            id,
                            this.reflectOrder(this.datafieldValidationRules)
                        )
                        .then(() => {
                            showInfoMessage(
                                isNew
                                    ? this.trans(
                                          'pages.edit_producttype.success_new_producttype_saved'
                                      )
                                    : this.trans(
                                          'pages.edit_producttype.success_changes_saved'
                                      )
                            );
                            this.$router.push('/producttypeDetails/' + id);
                        });
                })
                .catch((error) => {
                    return this.translateError(error);
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        translateError(error) {
            if (!error.response) {
                return showErrorMessage(
                    this.trans('pages.edit_producttype.error_unknown')
                );
            }
            const response = error.response.data;
            if (response && response.code) {
                switch (response.code) {
                    case 'product.validatename.required':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_key_required'
                            )
                        );
                    case 'product.validatename.unique':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_key_unique'
                            )
                        );
                    case 'product.validateproductdescription.placeholder':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_placeholder_product_description',
                                {
                                    language: response.language,
                                    info: response.info,
                                }
                            )
                        );
                    case 'product.validateprositedescription.placeholder':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_placeholder_prosite_description',
                                {
                                    language: response.language,
                                    info: response.info,
                                }
                            )
                        );
                    case 'product.validatecompactdescription.placeholder':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_placeholder_compact_description',
                                {
                                    language: response.language,
                                    info: response.info,
                                }
                            )
                        );
                    case 'product.validatequotedescription.placeholder':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_placeholder_quote_description',
                                {
                                    language: response.language,
                                    info: response.info,
                                }
                            )
                        );
                    case 'product.validatepaintlistdescription.placeholder':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_placeholder_paintlist_description',
                                {
                                    info: response.info,
                                }
                            )
                        );
                    case 'product.pleatingCreationFilter.noexists':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_pleating_creation_filter',
                                {
                                    info: response.info,
                                }
                            )
                        );
                    case 'product.pleatingOverviewInfo.noexists':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_pleating_overview_info',
                                {
                                    info: response.info,
                                }
                            )
                        );
                    case 'productTypeImage.removeFail':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_remove_image'
                            )
                        );
                    case 'productTypeImage.fileType':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_image_file_type'
                            )
                        );
                    case 'productTypeImage.uploadFail':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_upload_image'
                            )
                        );
                    case 'productTypeImage.saveFail':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_save_image_path'
                            )
                        );
                    case 'product.numberOfProductLabels.minValue':
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_producttype.error_product_labels_min'
                            )
                        );
                    case 'product.colli.minValue':
                        return showErrorMessage(
                            this.trans('pages.edit_producttype.error_colli_min')
                        );
                    case 'product_types.area_calculation_formula.validation.invalid_format':
                    case 'product_types.area_calculation_formula.validation.invalid_datafield':
                    case 'product_types.paint_area_calculation_formula.validation.invalid_format':
                    case 'product_types.paint_area_calculation_formula.validation.invalid_datafield':
                        return showErrorMessage(
                            this.trans(response.code, response.info || [])
                        );
                    default:
                        return showErrorMessage(
                            this.trans('pages.edit_producttype.error_unknown')
                        );
                }
            }

            return showErrorMessage(
                'Error: ' + ErrorHelper.parseError(error.response.data)
            );
        },
        load() {
            this.loading = true;
            const promises = [];
            let receivedType = null;
            if (!this.isNew) {
                this.productTypeId = this.idParam;

                promises.push(
                    productTypeApi
                        .fetchForEdit(this.productTypeId)
                        .then((response) => {
                            receivedType = response.data.data;
                            const productTypeName =
                                receivedType.translations['label'][
                                    receivedType.defaultLanguage.code
                                ];
                            const pageTitle = this.trans(
                                'pages.edit_producttype.update_product_type',
                                { name: productTypeName }
                            );
                            this.$store.commit(
                                'general/setPageTitle',
                                pageTitle,
                                { root: true }
                            );
                        })
                );
            } else {
                const pageTitle = this.trans(
                    'pages.edit_producttype.add_product_type'
                );
                this.$store.commit('general/setPageTitle', pageTitle, {
                    root: true,
                });
            }

            promises.push(
                languageApi.fetchAll().then((response) => {
                    this.languages = response.data.data;
                })
            );

            promises.push(
                statusApi.fetchAllStatuses().then((response) => {
                    this.stateOptions = response.data.data.map((x) => {
                        return {
                            value: x.code,
                            label: x.external_name + ' (' + x.code + ')',
                        };
                    });
                })
            );

            promises.push(
                orderApi.fetchAllOrderTypes().then((response) => {
                    const options = response.data.data.map((x) => {
                        return { value: x, label: x };
                    });
                    this.orderTypeOptions = options;
                })
            );

            promises.push(
                workstationApi.fetchAllTypes(null, false).then((response) => {
                    const options = response.data.data.map((x) => {
                        return { value: x.id, label: x.externalName };
                    });
                    options.unshift({ value: -1, label: '' });
                    this.workstationTypeOptions = options;
                })
            );

            promises.push(
                flowApi.fetchAllFlows().then((response) => {
                    const options = response.data.data.map((x) => {
                        return { value: x.flowId, label: x.label };
                    });
                    options.unshift({ value: -1, label: '' });
                    this.flowOptions = options;
                })
            );

            promises.push(
                supplierApi.fetchAllSuppliers().then((response) => {
                    const options = response.data.data.map((x) => {
                        return { value: x.id, label: x.name };
                    });
                    options.unshift({ value: -1, label: '' });
                    this.supplierOptions = options;
                })
            );

            promises.push(
                productTypeApi.listGroups().then((response) => {
                    const options = response.data.data.map((x) => {
                        return { value: x.id, label: x.name };
                    });
                    options.unshift({ value: -1, label: '' });
                    this.groupOptions = options;
                })
            );

            promises.push(
                productTypeApi.listSubgroups().then((response) => {
                    const options = response.data.data.map((x) => {
                        return { value: x.id, label: x.name };
                    });
                    options.unshift({ value: -1, label: '' });
                    this.subgroupOptions = options;
                })
            );

            if (!this.isNew) {
                promises.push(
                    productTypeApi
                        .fetchDataFields(this.productTypeId)
                        .then((response) => {
                            this.datafields = response.data.data;
                            const options = response.data.data.map((x) => {
                                return {
                                    value: x.datafieldId,
                                    label: x.name + ' (' + x.label + ')',
                                };
                            });
                            options.unshift({ value: -1, label: '' });
                            this.datafieldOptions = options;
                        })
                );

                promises.push(
                    productTypeApi
                        .fetchDatafieldValidationRules(this.productTypeId)
                        .then((response) => {
                            this.datafieldValidationRules =
                                response.data.datafieldValidationRules || [];
                        })
                );
            }

            promises.push(
                productTypeApi.fetchCustomImageOptions().then((response) => {
                    this.customImageOptions = response.data.data;
                })
            );

            promises.push(
                productTypeGroupApi.list().then((response) => {
                    this.productTypeGroups = response.data;
                })
            );

            Promise.allSettled(promises).then(() => {
                this.loading = false;
            });

            Promise.all(promises).then(() => {
                let type = this.newType();
                if (receivedType) {
                    type = { ...type, ...receivedType };
                }
                type.paintlistDescription = type.paintlistDescription || '';
                type.orderType =
                    type.orderType || this.orderTypeOptions[0].value;
                this.productType = type;
            });
        },
        addRule() {
            this.datafieldValidationRules.push({
                datafieldValidationConditions: [
                    {
                        datafieldId: null,
                        sort: 0,
                        cmpOperator: '=',
                        cmpValue: null,
                        cmpDatafieldId: null,
                        cmpDatafieldOptionId: null,
                        actionOperator: null,
                        actionValue: null,
                        actionDatafieldId: null,
                        actionDatafieldOptionId: null,
                    },
                ],
                datafieldValidationActions: [
                    {
                        datafieldId: null,
                        sort: 0,
                        action: null,
                        cmpOperator: '=',
                        cmpValue: null,
                        cmpDatafieldId: null,
                        cmpDatafieldOptionId: null,
                        actionOperator: null,
                        actionValue: null,
                        actionDatafieldId: null,
                        actionDatafieldOptionId: null,
                    },
                ],
            });
        },
        deleteRule(index) {
            this.datafieldValidationRules.splice(index, 1);
        },
        onOrderMoveUp(index) {
            const element = this.datafieldValidationRules[index];
            this.datafieldValidationRules.splice(index, 1);
            this.datafieldValidationRules.splice(index - 1, 0, element);
        },
        onOrderMoveDown(index) {
            const element = this.datafieldValidationRules[index];
            this.datafieldValidationRules.splice(index, 1);
            this.datafieldValidationRules.splice(index + 1, 0, element);
        },
        reflectOrder(array) {
            return array.map((item, index) => {
                item.order = index;

                return item;
            });
        },
    },
    mixins: [IsDebugMixin, IdParamMixin(), localization],
    computed: {
        productTypeGroupOptions() {
            if (!this.productTypeGroups) {
                return null;
            }
            return this.productTypeGroups
                .map((group) => ({
                    value: group.id,
                    label: getItemPath(
                        group.id,
                        this.productTypeGroups,
                        this.isDebug
                    ),
                }))
                .sort(byLabelSorter);
        },
        // Bulk copy can only be enabled for product types with width and height datafields
        hasRequiredBulkCopyDatafields() {
            if (!this.datafields) {
                return false;
            }

            return BULK_COPY_REQUIRED_DATAFIELD_KEYS.every((key) => {
                return this.datafields.some((datafield) => {
                    return datafield.name === key;
                });
            });
        },
        isNew() {
            const result =
                !this.$router.history.current.path.startsWith(
                    '/editProducttype'
                );
            return result;
        },
        savePayload() {
            if (!this.productType) {
                return null;
            }

            const payload = JSON.parse(JSON.stringify(this.productType));

            const translations = payload.translations;

            delete payload.translations;

            for (const [key, value] of Object.entries(translations)) {
                payload[key] = value;
            }

            payload.workstationType = payload.workstationTypeId
                ? payload.workstationTypeId
                : null;
            delete payload.workstationTypeId;

            payload.paintlistDescription = payload.paintlistDescription;

            delete payload.imagePathCloudFront;

            payload.producttypeId = parseInt(payload.producttypeId);
            payload.supplierId = parseInt(payload.supplierId);
            payload.profilegroup = parseInt(payload.profilegroup);
            payload.profilesubgroup = parseInt(payload.profilesubgroup);
            payload.defaultFlow = parseInt(payload.defaultFlow);
            payload.productTypeGroupId = payload.productTypeGroupId
                ? parseInt(payload.productTypeGroupId)
                : null;

            return payload;
        },
        productAttachmentRules: () => productAttachmentRules,
    },
};
</script>
